import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { PaymentIntent, SetupIntent, StripeError } from '@stripe/stripe-js'
import { uniq, without } from 'ramda'

import i18n from '../../../locales/i18n'
import { BillingAddress, ClassesType, Nil, User } from '../../../types'
import StripeElementWrapper from './StripeElementWrapper'
import { billingAddressToStripeFormat } from './stripeUtils'

const useStyles = makeStyles(
  theme => ({
    root: {},
    inputContainer: {
      '&:not(:first-of-type)': {
        marginTop: theme.spacing(5),
      },
    },
    textField: {
      maxWidth: 275,
    },
  }),
  { name: 'StripePaymentForm' },
)

const elementsList = [CardNumberElement, CardCvcElement, CardExpiryElement]

type StripePaymentResponse = {
  error?: StripeError | string
  paymentIntent?: PaymentIntent
  setupIntent?: SetupIntent
}

export interface StripePaymentFormHandle {
  createPayment: (paymentProps: PaymentProps) => Promise<StripePaymentResponse>
}

export interface StripePaymentFromProps {
  readonly classes?: ClassesType<typeof useStyles>
  readonly onCompleteStateChange: (complete: boolean) => void
}

export type PaymentProps = {
  billingAddress: BillingAddress
  client: User | Nil
  clientSecret: string | Nil
  method?: 'confirmCardSetup' | 'confirmCardPayment'
}

const StripePaymentForm = forwardRef<
StripePaymentFormHandle,
StripePaymentFromProps
>(function StripePaymentForm(
  { classes: classesProp, onCompleteStateChange },
  ref,
) {
  const classes = useStyles({ classes: classesProp })
  const stripe = useStripe()
  const elements = useElements()

  const CLIENT_SECRET_MISSING_ERROR = i18n.t(
    'Errors:CLIENT_SECRET_MISSING_ERROR',
  )
  const BILLING_ADDRESS_MISSING_ERROR = i18n.t(
    'Errors:BILLING_ADDRESS_MISSING_ERROR',
  )
  const STRIPE_NOT_READY_ERROR = i18n.t('Errors:STRIPE_NOT_READY_ERROR')

  const [completeElements, setCompleteElements] = useState<any[]>([])

  useImperativeHandle(ref, () => ({
    createPayment: ({
      client,
      clientSecret,
      billingAddress,
      method = 'confirmCardSetup',
    }: PaymentProps) => {
      if (!clientSecret) {
        return Promise.resolve({
          error: CLIENT_SECRET_MISSING_ERROR,
        })
      }

      if (!billingAddress) {
        return Promise.resolve({
          error: BILLING_ADDRESS_MISSING_ERROR,
        })
      }

      if (!elements || !stripe) {
        return Promise.resolve({
          error: STRIPE_NOT_READY_ERROR,
        })
      }

      const cardNumberElement = elements.getElement(CardNumberElement)

      const stripeDataObject = {
        payment_method: {
          type: 'card',
          card: cardNumberElement,
          billing_details: billingAddressToStripeFormat(client, billingAddress),
        },
      }

      // TODO: remove method and split into multiple functions
      // @ts-ignore
      return stripe[method](clientSecret, stripeDataObject)
    },
  }))

  const createStripeElementChangeHandler = (name: any) => (element: any) => {
    const newCompleteElements =
      !element.empty && element.complete
        ? uniq(completeElements.concat(name))
        : without([name], completeElements)

    const oldCompleteState = completeElements.length >= elementsList.length
    const newCompleteState = newCompleteElements.length >= elementsList.length

    const completeStateChanged = oldCompleteState !== newCompleteState

    if (completeStateChanged && onCompleteStateChange) {
      onCompleteStateChange(newCompleteState)
    }

    setCompleteElements(newCompleteElements)
  }

  return (
    <Grid container item className={classes.root} direction="column">
      <Grid item className={classes.inputContainer}>
        <StripeElementWrapper
          className={classes.textField}
          component={CardNumberElement}
          label={i18n.t<string>('Common:PAYMENTS.CARD_NUMBER')}
          onChange={createStripeElementChangeHandler(CardNumberElement)}
        />
      </Grid>
      <Grid item className={classes.inputContainer}>
        <StripeElementWrapper
          className={classes.textField}
          component={CardCvcElement}
          label={i18n.t<string>(
            'Common:PAYMENTS.CARD_VERIFICATION_VALUE_ABBREVIATION',
          )}
          onChange={createStripeElementChangeHandler(CardCvcElement)}
        />
      </Grid>
      <Grid item className={classes.inputContainer}>
        <StripeElementWrapper
          className={classes.textField}
          component={CardExpiryElement}
          label={i18n.t<string>('Common:PAYMENTS.EXPIRATION_DATE')}
          onChange={createStripeElementChangeHandler(CardExpiryElement)}
        />
      </Grid>
    </Grid>
  )
})

export default StripePaymentForm
